import React from "react";
import WidgetCard from "@ui/WidgetCard";
import t from "../../../../../i18n";
import Statistic from "@ui/Statistic";
import type { Setup } from "../../../../../api/useSetups";
import type { ActiveAnalysis } from "../../../../../api/useActiveAnalysesStream";
import type { Project } from "../../../../../api/useProjects";
import { getFilteredFailureValue } from "../../../Chart/series/getFailureSeries";
import { useIndexedFailureValues } from "../../../../../api/useIndexedFailureValues";
import { useIndexedManualFailureValues } from "../../../../../api/useManualFailureValues";
import { comparatorBy } from "diagnostics-lib/comparator";
import type { ColumnMetaData } from "@ui/MiniTable";
import formatNumber from "@ui/i18n/formatNumber";
import Ellipsis from "@ui/Ellipsis";
import { styled } from "@linaria/react";
import FailureIcon, { NO_CONFIDENCE_INDICATOR } from "../FailureIcon";
import { getFailureColor } from "../../../useFailureColor";
import ReparabilityTag from "../../../Settings/Setup/Diagnosis/Failures/Reparability";
import { EmptyMark } from "@ui/Table/Td";
import MiniTable from "@ui/MiniTable";
import Selector from "@ui/Selector";
import createGlobalState from "@ui/lib/createGlobalState";
import * as io from "io-ts";
import { color } from "@ui";
import computeInternalGap from "../../../../../domain/computeInternalGap";
import { useManualFailures } from "../../../../../api/useManualFailures";
import { useTranslations } from "../../../../../api/useTranslations";
import computeUnexplainedGap from "../../../../../domain/computeUnexplainedGap";
import { useIndexedFailureMetadata } from "../../../../../api/useFailureMetadata";
import type { FailureValueStatus } from "../../../Chart/PointMetadata";
import type { Reparability } from "../../../../../api/Reparability";
import { type Quantity } from "../../../../../api/useQuantities";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import { isAnalysisQuantityValid } from "../../../metrology";
import { isFaultPromoted } from "../../../../../domain/isFaultPromoted";

const FailureWidgetCard = styled(WidgetCard)`
  height: max-content;
  /* Override default WidgetCard row-gap (css specificity issue) */
  row-gap: 16px !important;
`;

const Wrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const CellWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const ContainerTotal = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;
`;
const Total = styled.span`
  display: flex;
  justify-content: flex-start;
  white-space: nowrap;
`;
const TotalTitle = styled.span`
  color: ${color.carbon700};
  padding-right: 5px;
`;
const TotalResult = styled.span`
  color: ${color.carbon900};
  font-weight: 600;
  width: 100%;
`;

const StickyTable = styled.div`
  position: relative;
  table thead {
    position: sticky;
    top: 0;
  }
`;

const FAILURE = "failure";
const ColorIndicator = styled.span<{ color: string }>`
  display: block;
  width: 8px;
  height: 8px;
  background-color: ${p => p.color};
  margin-right: 12px;
  &.${FAILURE} {
    border-radius: 100%;
  }
`;

export const useSelector = createGlobalState({
  storage: "session",
  key: "summaryFailuresSelector",
  type: io.union([io.literal("performance"), io.literal("maintenance")]),
  scoped: true,
});

interface P {
  project: Project;
  setups: Setup[];
  activeAnalysis: ActiveAnalysis;
  quantitiesOfInterest: Quantity[];
}

type Data = {
  name: string;
  displayName: string | null;
  isManual: boolean;
  confidenceIndicator: number;
  value: number | null;
  losses: { [key: string]: number | null };
  unit: string | null;
  system: string | null;
  equipmentType: string | null;
  failureCategory: string | null;
  degradationType: string | null;
  equipments: string | null;
  suspectedElements: string | null;
  reparability: Reparability | null;
  isPromoted: boolean | null;
};

function computeRowKey(failure: Data) {
  return failure.name;
}

const optionsSelector = [
  { label: "Performance", value: "performance" },
  { label: "Maintenance", value: "maintenance" },
] as const;

const confirmedSet = new Set<FailureValueStatus>(["CONFIRMED"]);

export function Failures({ project, setups, activeAnalysis, quantitiesOfInterest }: P) {
  const translations = useTranslations();
  const setup = getAnalysisSetup(setups, activeAnalysis);
  const indexedFailureValues = useIndexedFailureValues(project.id);
  const indexedFailureMetadata = useIndexedFailureMetadata(project.id);
  const indexedManualFailureValues = useIndexedManualFailureValues(project.id);
  const [selector, setSelector] = useSelector({
    scope: project.id,
    defaultValue: "performance",
  });

  const confirmedFailures = React.useMemo(
    () =>
      setup === null
        ? []
        : setup.failures.filter(f => {
            const status = indexedFailureMetadata.getFailureStatus(activeAnalysis.date, f.name);
            const idConfirmed = getFilteredFailureValue(
              f.name,
              activeAnalysis.date,
              indexedFailureValues,
              status,
              confirmedSet,
            );
            return idConfirmed !== undefined;
          }),
    [activeAnalysis.date, indexedFailureMetadata, indexedFailureValues, setup],
  );

  const dataConfirmedFailures = React.useMemo(
    () =>
      confirmedFailures
        .flatMap(failure => {
          const failureValue = indexedFailureValues[activeAnalysis.date]?.[failure.name];

          if (failureValue === undefined) return [];

          const losses = quantitiesOfInterest.reduce<{ [key: string]: number | null }>(
            (acc, qoi) => {
              const key = qoi.name;
              acc[key] = failureValue.impacts[qoi.id] ?? null;
              return acc;
            },
            {},
          );

          const result: Data = {
            name: failure.name,
            displayName: failure.displayName,
            isManual: false,
            confidenceIndicator: failureValue.confidence ?? NO_CONFIDENCE_INDICATOR,
            losses,
            value: failureValue.value,
            unit: failure.unit,
            system: failure.systemId,
            equipmentType: failure.equipmentTypeId,
            failureCategory: failure.failureCategoryId,
            degradationType: failure.degradationTypeId,
            equipments:
              failure.equipments.length === 0
                ? null
                : failure.equipments.map(e => e.equipment.name).join(", "),
            suspectedElements:
              failure.suspectedElements.length === 0
                ? null
                : failure.suspectedElements.map(e => e.suspectedElement.name).join(", "),
            reparability:
              indexedFailureMetadata.getFailureReparabilty(activeAnalysis.date, failure.name) ||
              failure.reparability,
            isPromoted: isFaultPromoted(activeAnalysis.quantityValues, failure.filters),
          };

          return [result];
        })
        .sort(comparatorBy("name")) ?? [],
    [
      activeAnalysis,
      confirmedFailures,
      indexedFailureValues,
      quantitiesOfInterest,
      indexedFailureMetadata,
    ],
  );

  const manualFailures = useManualFailures(project.id);
  const dataManualFailures = React.useMemo(
    () =>
      manualFailures
        .flatMap(failure => {
          const manualFailureValue = indexedManualFailureValues[activeAnalysis.id]?.[failure.id];
          if (!manualFailureValue) return [];

          const losses = quantitiesOfInterest.reduce<{ [key: string]: number | null }>(
            (acc, qoi) => {
              const key = qoi.name;
              acc[key] = manualFailureValue.impacts[qoi.id] ?? null;
              return acc;
            },
            {},
          );

          const result: Data = {
            name: failure.name,
            displayName: null,
            isManual: true,
            confidenceIndicator: NO_CONFIDENCE_INDICATOR,
            losses,
            value: null,
            unit: null,
            system: failure.systemId,
            equipmentType: failure.equipmentTypeId,
            failureCategory: failure.failureCategoryId,
            degradationType: failure.degradationTypeId,
            equipments:
              failure.equipments.length === 0
                ? null
                : failure.equipments.map(e => e.equipment.name).join(", "),
            suspectedElements:
              failure.suspectedElements.length === 0
                ? null
                : failure.suspectedElements.map(e => e.suspectedElement.name).join(", "),
            reparability: manualFailureValue.reparability,
            isPromoted: null,
          };

          return [result];
        })
        .sort(comparatorBy("name")),
    [manualFailures, activeAnalysis, indexedManualFailureValues, quantitiesOfInterest],
  );

  const data = React.useMemo(
    () => [...dataConfirmedFailures, ...dataManualFailures],
    [dataConfirmedFailures, dataManualFailures],
  );

  const commonMetadata: { [key: string]: ColumnMetaData<Data> } = React.useMemo(
    () => ({
      name: {
        label: t("summary.failures.table.failures"),
        render(failure) {
          if (failure.name === undefined) return null;
          return (
            <CellWrapper>
              <ColorIndicator
                className={failure.isManual ? undefined : FAILURE}
                color={getFailureColor(
                  project,
                  failure.isManual ? "MANUAL_FAILURE" : "FAILURE",
                  failure.name,
                )}
              />
              <Ellipsis>{failure.isManual ? failure.name : failure.displayName}</Ellipsis>
              <FailureIcon failure={failure} />
            </CellWrapper>
          );
        },
        minWidth: "230px",
      },
      amplitude: {
        showEmptyMark: true,
        label: t("summary.failures.table.amplitude"),
        render(failureValue) {
          if (failureValue.isManual || failureValue.value === null) return null;
          return `${formatNumber(failureValue.value, {
            unit: failureValue.unit === null ? undefined : failureValue.unit,
            precision: 1,
            sign: false,
          })}`;
        },
        alignment: "left",
      },
    }),
    [project],
  );

  const maintenanceOwnMetadata: { [key: string]: ColumnMetaData<Data> } = React.useMemo(
    () => ({
      degradationType: {
        showEmptyMark: true,
        label: t("summary.failures.table.degradationType"),
        render(failureValue) {
          return failureValue.degradationType;
        },
        minWidth: "150px",
      },
      failureCategory: {
        showEmptyMark: true,
        label: t("summary.failures.table.failureCategory"),
        render(failureValue) {
          return failureValue.failureCategory;
        },
        minWidth: "100px",
      },
      reparability: {
        showEmptyMark: true,
        label: t("summary.failures.table.reparability"),
        render(failureValue) {
          if (!failureValue.reparability) return null;
          return (
            <ReparabilityTag reparability={failureValue.reparability} translations={translations} />
          );
        },
        minWidth: "100px",
      },
      system: {
        showEmptyMark: true,
        label: t("summary.failures.table.system"),
        render(failureValue) {
          return failureValue.system;
        },
        minWidth: "100px",
      },
      equipments: {
        showEmptyMark: true,
        label: t("summary.failures.table.equipments"),
        render(failureValue) {
          return failureValue.equipments;
        },
        minWidth: "100px",
      },
      equipmentType: {
        showEmptyMark: true,
        label: t("summary.failures.table.equipmentType"),
        render(failureValue) {
          return failureValue.equipmentType;
        },
        minWidth: "150px",
      },
      suspectedElements: {
        showEmptyMark: true,
        label: t("summary.failures.table.suspectedElements"),
        render(failureValue) {
          return failureValue.suspectedElements;
        },
        minWidth: "150px",
      },
    }),
    [translations],
  );

  const performanceOwnMetadata = React.useMemo(
    () =>
      quantitiesOfInterest.reduce<{
        [key: string]: ColumnMetaData<Data>;
      }>((acc, qoi, index) => {
        const name = qoi.name;
        acc[name] = {
          showEmptyMark: true,
          label: t("summary.failures.table.loss", name),
          render(failureValue) {
            const loss = failureValue.losses[name];
            return loss === undefined
              ? null
              : `${formatNumber(loss === null ? 0 : loss, {
                  unit: qoi.unit ?? undefined,
                  precision: 1,
                  sign: true,
                })}`;
          },
          renderFooter() {
            const simulationOutput = setup?.simulationOutputs.find(s => s.quantityId === qoi.id);
            const computeInternalGapValue = computeInternalGap(
              activeAnalysis,
              qoi.id,
              simulationOutput?.offsetExpected ?? 0,
            );
            const computedUnexplainedGap = computeUnexplainedGap(
              activeAnalysis,
              indexedFailureValues,
              indexedFailureMetadata,
              indexedManualFailureValues,
              qoi.id,
              confirmedSet,
              simulationOutput?.offsetExpected ?? 0,
            );

            const lossesTotal = computeInternalGapValue;
            const validData = data.map(d => d.losses[name]).filter(f => f !== undefined);

            const valid = isAnalysisQuantityValid(activeAnalysis, qoi);

            const lossessExplained =
              validData.length > 0
                ? validData.reduce((acc, loss) => (acc ?? 0) + (loss ?? 0), 0)
                : null;

            const percentLosses =
              lossessExplained !== null && lossesTotal !== null
                ? (lossessExplained / lossesTotal) * 100
                : null;

            if (lossesTotal === null) {
              return <EmptyMark />;
            } else {
              const value = formatNumber(Number(lossessExplained), {
                unit: qoi.unit ?? undefined,
                precision: 1,
                sign: true,
              });
              const unexplainedGap =
                valid && computedUnexplainedGap !== null
                  ? formatNumber(computedUnexplainedGap, {
                      unit: qoi.unit ?? undefined,
                      precision: 1,
                      sign: true,
                    })
                  : "-";
              const rate =
                valid && percentLosses !== null
                  ? formatNumber(Number(percentLosses), {
                      unit: "%",
                      precision: 1,
                      sign: Number(percentLosses) < 0 ? true : false,
                    })
                  : "-";
              return (
                <ContainerTotal>
                  <Total>
                    {index === 0 && <TotalTitle>Total</TotalTitle>}
                    <TotalResult>{value}</TotalResult>
                  </Total>
                  <Total>
                    {index === 0 && (
                      <TotalTitle>{t("charts.legend.unexplained_losses", translations)}</TotalTitle>
                    )}
                    {unexplainedGap ? <TotalResult>{unexplainedGap}</TotalResult> : <EmptyMark />}
                  </Total>
                  <Total>
                    {index === 0 && <TotalTitle>{t("summary.widget.failures.rate")}</TotalTitle>}
                    <TotalResult>{rate}</TotalResult>
                  </Total>
                </ContainerTotal>
              );
            }
          },
          alignment: "right",
          minWidth: "150px",
        };
        return acc;
      }, {}),
    [
      quantitiesOfInterest,
      setup?.simulationOutputs,
      activeAnalysis,
      indexedFailureValues,
      indexedManualFailureValues,
      indexedFailureMetadata,
      data,
      translations,
    ],
  );

  const performanceMetadata: { [key: string]: ColumnMetaData<Data> } = React.useMemo(
    () => ({ ...commonMetadata, ...performanceOwnMetadata }),
    [commonMetadata, performanceOwnMetadata],
  );

  const maintenanceMetadata: { [key: string]: ColumnMetaData<Data> } = React.useMemo(
    () => ({ ...commonMetadata, ...maintenanceOwnMetadata }),
    [commonMetadata, maintenanceOwnMetadata],
  );

  return (
    <FailureWidgetCard title={t("commom.widgets.failures.title")} data-cy="failures-section">
      <Statistic title={t("summary.widget.failures.confirmed_failures")} value={data.length} />
      {data.length > 0 && (
        <>
          <Selector options={optionsSelector} onChange={setSelector} value={selector} />
          <Wrapper>
            <StickyTable>
              <MiniTable
                data-cy="failures-table"
                metadata={selector === "performance" ? performanceMetadata : maintenanceMetadata}
                data={data}
                computeRowKey={computeRowKey}
              />
            </StickyTable>
          </Wrapper>
        </>
      )}
    </FailureWidgetCard>
  );
}

export default React.memo(Failures);
